import {Expressions, type ExpressionPath} from '@packages/systems/wfdl';
import {type Expression} from '@packages/systems/wfdl-ast';
import {
  ASSET_ENV_REGISTRY,
  buildS3CdnMap,
  type S3CdnMap,
} from '@packages/utilities/asset-env-registry';

// Respnonsive Utils shared between the designer, editor, and server.
export const COMPAT_RESPONSIVE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'avif',
] as const;
export const COMPAT_RESPONSIVE_REGEX = new RegExp(
  '(\\.(' + COMPAT_RESPONSIVE_EXTENSIONS.join('|') + '))$',
  'i'
);
export const VARIANT_WIDTHS = [500, 800, 1080, 1600, 2000, 2600, 3200] as const;
export const MIN_VIEWPORT_SIZE = 240;
export const MAX_VIEWPORT_SIZE = 10000;
export const DEFAULT_SIZES_VAL = '100vw';

// Per-env S3/CDN URL roots. Runtime S3<->CDN conversion now resolves from the
// asset-env registry (see _SRCSET_S3_CDN_MAP below), so these are retained only
// as fixtures for a handful of downstream tests, not for production code paths.
export const DEV_CDN_ROOT = 'https://dev-assets.website-files.com/';
export const DEV_CDN_CLOUDFLARE = 'https://cdn.dev.website-files.com/';
// eslint-disable-next-line webflow/no-hardcoded-s3-endpoints -- test-only fixture: delete in follow-up
export const DEV_S3_ROOT = 'https://s3.amazonaws.com/webflow-dev-assets/';
export const PROD_CDN_CLOUDFLARE = 'https://cdn.prod.website-files.com/';
// eslint-disable-next-line webflow/no-hardcoded-s3-endpoints -- test-only fixture: delete in follow-up
export const PROD_S3_ROOT = 'https://s3.amazonaws.com/webflow-prod-assets/';

const _SRCSET_S3_CDN_MAP: S3CdnMap = buildS3CdnMap(ASSET_ENV_REGISTRY, [
  {
    s3bucket: 'wf-app-assets-ci2',
    cloudFlareCdn: 'fake-cloudflare-ci2.ci2.us-east-1.wfci.io',
  },
  {
    // CI file uploads (DLOOP-7771); served through the same fake-cloudflare
    // distribution as wf-app-assets-ci2.
    s3bucket: 'ephemeral-wf-file-uploads-ci2',
    cloudFlareCdn: 'fake-cloudflare-ci2.ci2.us-east-1.wfci.io',
  },
  {
    s3bucket: 'wf-app-assets-ci2staging',
    cloudFlareCdn: 'fake-cloudflare-ci2staging.ci2staging.us-east-1.wfci.io',
  },
]);

type AnyAsset = Partial<{
  [key: string]: any;
}>;
/**
 * Generates a srcset attribute value, derived from an asset's variants
 * @param  {Object} asset      An asset (file, dbFile, asset)
 * @param  {Function} processUrl a function to process urls, typically used to modify the domain root on publish
 * @return {string}            The compiled srcsetValue
 */
export function getSrcset(
  asset?: AnyAsset,
  processUrl?: (url: string) => string
): string {
  if (!asset) {
    return '';
  }

  const cdnUrl = asset.cdnUrl || s3UrlToCdn(asset.s3Url);

  if (isResponsiveCompatible(cdnUrl)) {
    const srcsetArray: Array<any | string> = [];
    const foundWidths: Array<any> = [];
    const variants = asset.variants;
    const responsiveVariants: Record<string, any> = {};

    if (variants && variants.length) {
      // Iterate over variants from newest to oldest, in cases where we have multiple versions of the same
      // width, use the most recent.
      for (
        let i = variants.length - 1;
        i >= 0 && foundWidths.length < VARIANT_WIDTHS.length;
        i--
      ) {
        const variant = variants[i];
        const variantWidth = variant.width;
        const isResponsiveVariant =
          variant.quality === 100 &&
          VARIANT_WIDTHS.indexOf(variantWidth) !== -1;
        const generationError = variant.error;
        const isUnique = foundWidths.indexOf(variantWidth) === -1;

        // Only include top-notch responsive variants
        if (isResponsiveVariant && isUnique && !generationError) {
          const url = s3UrlToCdn(variant.s3Url);
          const size = variant.size;

          const variantUrl = processUrl ? processUrl(url) : url;

          if (variantUrl && variantWidth) {
            responsiveVariants[variantWidth] = {
              processedUrl: variantUrl,
              size,
            };
            foundWidths.push(variantWidth);
          }
        }
      }

      // If filesize information is available, use to exclude responsive variants that break the 'too large rule'
      // ie: if a variant has larger filesize than one of it's larger width siblings or master image
      // Note: in the client the master image's fileSize is reified as asset.fileSize, on the server it's asset.size
      let variantSizeCap = asset.fileSize || asset.size;

      // Loop from largest to smallest to drop the 'too large' variants and create some kind of order.
      for (let j = VARIANT_WIDTHS.length - 1; j >= 0; j--) {
        const width = VARIANT_WIDTHS[j];
        // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
        const meta = responsiveVariants[width];
        if (meta) {
          const processedUrl = meta.processedUrl;
          const variantSize = meta.size;
          const checkVariantSize = variantSizeCap && variantSize;
          const variantTooLarge =
            checkVariantSize && variantSize >= variantSizeCap;

          if (processedUrl && !variantTooLarge) {
            srcsetArray.push(processedUrl + ' ' + width + 'w');

            // Make sure to update the fileSizeCap so the next variant is smaller than this one
            if (variantSizeCap && variantSize) {
              variantSizeCap = variantSize;
            }
          }
        }
      }
    }

    if (srcsetArray.length) {
      // Ensure variant order from smallest to largest for better code readability
      srcsetArray.reverse();

      // NOTE: there should always be a width, but in the event there isn't one
      // we'd rather not include the asset as a srcset option.
      const assetWidth = asset.width;
      if (assetWidth) {
        // add in the original
        const masterUrl = processUrl ? processUrl(cdnUrl) : cdnUrl;
        srcsetArray.push(masterUrl + ' ' + assetWidth + 'w');
      }

      return srcsetArray.join(', ');
    }
  }
  return '';
}

/**
 * parses the urls out of a srcset value
 * @param  {String} srcsetVal eg. 'http://image.jpg 400w, http://image-big.jpg'
 * @return {Array}            An array of srcet urls
 */
export function getUrlsFromSrcsetVal(srcsetVal?: string | null): Array<string> {
  const urls: Array<string> = [];

  if (typeof srcsetVal === 'string') {
    srcsetVal
      // split at comma separating each srcset item
      .split(',')
      .forEach(function (srcsetItem) {
        if (srcsetItem.length) {
          const url = srcsetItem
            // remove the width descriptor if it exists. eg: "http://some url.jpg 500w"
            .replace(/ \d+(w|x)$/, '')
            // trim the url, since there will usually be at least a leading space after the comma separator.
            .trim();

          urls.push(url);
        }
      });
  }

  return urls;
}

/**
 * Compiles an array of sizes values into a sizes attribute value
 * @param  {Array} sizesArr An array of objects {max: Number, size: String}
 * @return {String}         the sizes attribute value
 */
export function buildSizesString(sizesArr: any): string {
  const isArray = sizesArr instanceof Array;

  if (!isArray) {
    return '';
  } else if (sizesArr.length === 1) {
    return sizesArr[0].size;
  } else if (sizesArr.length > 1) {
    return sizesArr
      .map(function (size) {
        if (size.max === MAX_VIEWPORT_SIZE) {
          return String(size.size);
        } else {
          return '(max-width: ' + size.max + 'px) ' + size.size;
        }
      })
      .join(', ');
  } else {
    return '';
  }
}

const getTextFromPath = (
  expr: Expression,
  path: ExpressionPath
): string | null => {
  const exprAtPath = expr ? Expressions.getIn(expr, path) : null;
  return exprAtPath ? Expressions.getText(exprAtPath) : null;
};

const getNumberFromPath = (
  expr: Expression,
  path: ExpressionPath
): number | null => {
  const exprAtPath = expr ? Expressions.getIn(expr, path) : null;
  return exprAtPath ? Expressions.getNumber(exprAtPath) : null;
};

const SIZE_PATH: ExpressionPath = [{in: 'Record', at: 'size'}];
const MAX_PATH: ExpressionPath = [{in: 'Record', at: 'max'}];
export function buildSizesStringFromExpr(sizesExpr: Expression): string {
  const sizesArr = Expressions.getList(sizesExpr);

  if (!sizesArr) {
    return '';
  } else if (sizesArr.length === 1) {
    const firstItemSizeExpr = Expressions.getIn(sizesArr[0], SIZE_PATH);
    const size = firstItemSizeExpr && Expressions.getText(firstItemSizeExpr);
    return size || '';
  } else if (sizesArr.length > 1) {
    const mappedSizes = sizesArr.map((sizeExpr) => {
      const size = getTextFromPath(sizeExpr, SIZE_PATH);
      const max = getNumberFromPath(sizeExpr, MAX_PATH);
      if (max === MAX_VIEWPORT_SIZE) {
        return size || '';
      } else {
        return `(max-width: ${max}px) ${size}`;
      }
    });
    return mappedSizes.join(', ');
  } else {
    return '';
  }
}

/**
 * Converts an s3Url to a cdn url
 * @param  {String} s3Url
 * @return {String}
 */
export function s3UrlToCdn(s3Url: string): string {
  if (typeof s3Url === 'string') {
    for (const [s3Root, cfCdn] of _SRCSET_S3_CDN_MAP.s3ToCfCdn) {
      if (s3Url.includes(s3Root)) {
        return s3Url.replace(s3Root, cfCdn);
      }
    }
    return s3Url;
  } else {
    return '';
  }
}

/**
 * Converts an s3Url to a cdn ssl accelerated url
 * @param s3Url
 */
export function s3UrlToSslAcceleratedCdn(s3Url: string) {
  if (typeof s3Url === 'string') {
    for (const [s3Root, cfCdn] of _SRCSET_S3_CDN_MAP.s3ToCfCdn) {
      if (s3Url.includes(s3Root)) {
        return s3Url.replace(s3Root, cfCdn);
      }
    }
    return s3Url;
  } else {
    return '';
  }
}

/**
 * Converts a CdnUrl to an s3Url
 * @param  {String} cdnUrl
 * @return {String}
 */
export function cdnToS3Url(cdnUrl: string): string {
  if (typeof cdnUrl === 'string') {
    _SRCSET_S3_CDN_MAP.allCdnRegex.lastIndex = 0;
    return cdnUrl.replace(_SRCSET_S3_CDN_MAP.allCdnRegex, (match) => {
      return _SRCSET_S3_CDN_MAP.cdnToS3.get(match) || match;
    });
  } else {
    return '';
  }
}

/**
 * Converts any cdnUrls in a string to a url of your choosing
 * @param  {String} cdnUrl
 * @return {String}
 */
export function cdnToCustom(str: string, customUrl: string): string {
  if (typeof str !== 'string' || !customUrl) {
    return str;
  }
  _SRCSET_S3_CDN_MAP.wfCdnRegex.lastIndex = 0;
  return str.replace(_SRCSET_S3_CDN_MAP.wfCdnRegex, customUrl);
}

/**
 * Checks url or filename's extention for compatability with PRISM and good for responsive iamges
 * @param  {String}  str a url or filename
 * @return {Boolean}
 */
export function isResponsiveCompatible(urlOrFilename: string): boolean {
  return COMPAT_RESPONSIVE_REGEX.test(urlOrFilename);
}
